import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listRoles, createRole, updateRole, deleteRole } from '../../services/roles';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import DataTable from '../../components/ui/DataTable';

const RolesPermissions = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await listRoles();
      setRoles(response.data || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowAction = (action, row) => {
    const role = typeof row === 'string' ? roles.find(r => (r._id || r.id) === row) : row;
    if (!role) return;

    if (action === 'view') {
      const roleId = role._id || role.id;
      navigate(`/settings/role-details/${roleId}`);
    } else if (action === 'edit') {
      setEditingRole(role);
      setIsCreateModalOpen(true);
    } else if (action === 'delete') {
      setRoleToDelete(role);
      setIsDeleteModalOpen(true);
    }
  };


  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    
    try {
      await deleteRole(roleToDelete._id || roleToDelete.id);
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);
      await loadRoles();
    } catch (err) {
      console.error('Failed to delete role:', err);
      alert('Failed to delete role. Please try again.');
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setIsCreateModalOpen(true);
  };

  const handleSaveRole = async (roleData) => {
    try {
      if (editingRole) {
        await updateRole(editingRole._id || editingRole.id, roleData);
      } else {
        await createRole(roleData);
      }
      setIsCreateModalOpen(false);
      setEditingRole(null);
      await loadRoles();
    } catch (err) {
      console.error('Failed to save role:', err);
      alert('Failed to save role. Please try again.');
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  // Get role description based on role name
  const getRoleDescription = (roleName) => {
    const descriptions = {
      'Admin': 'Full access to manage users, Licences, settings, and approvals.',
      'Reviewer': 'Can review assigned Licences, communicate with requesters, and submit quotations.',
      'Approver': 'Can review submitted Licences and make final approval, rejection, or hold decisions.'
    };
    return descriptions[roleName] || roleName.description || 'No description available.';
  };

  // Default roles data structure matching Figma
  const defaultRoles = [
    {
      name: 'Admin',
      description: 'Full access to manage users, Licences, settings, and approvals.',
      staffCount: 2,
      dateCreated: '01/05/2026',
      lastAction: '01/05/2026',
      lastActionBy: { name: 'Wade Warren', avatar: null }
    },
    {
      name: 'Reviewer',
      description: 'Can review assigned Licences, communicate with requesters, and submit quotations.',
      staffCount: 12,
      dateCreated: '11/22/2025',
      lastAction: '11/22/2025',
      lastActionBy: { name: 'Ralph Edwards', avatar: null }
    },
    {
      name: 'Approver',
      description: 'Can review submitted Licences and make final approval, rejection, or hold decisions.',
      staffCount: 8,
      dateCreated: '03/15/2023',
      lastAction: '03/15/2023',
      lastActionBy: { name: 'Floyd Miles', avatar: null }
    }
  ];

  // Merge API data with defaults or use defaults if no API data
  const displayRoles = roles.length > 0 ? roles.map((role, idx) => ({
    ...role,
    id: role._id || role.id || idx,
    description: role.description || getRoleDescription(role.name),
    staffCount: role.userCount || role.staffCount || 0,
    dateCreated: formatDate(role.createdAt),
    lastAction: formatDate(role.updatedAt),
    lastActionBy: role.lastActionBy || { name: 'System', avatar: null }
  })) : defaultRoles.map((role, idx) => ({ ...role, id: `default-${idx}` }));

  // Action menu items
  const actionMenuItems = [
    {
      label: 'View',
      action: 'view',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10.3866 8.00001C10.3866 9.31334 9.31995 10.38 7.99995 10.38C6.67995 10.38 5.61328 9.31334 5.61328 8.00001C5.61328 6.68668 6.67995 5.62001 7.99995 5.62001C9.31995 5.62001 10.3866 6.68668 10.3866 8.00001Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.00004 13.5133C10.3534 13.5133 12.5467 12.1267 14.0734 9.72668C14.6734 8.78668 14.6734 7.20668 14.0734 6.26668C12.5467 3.86668 10.3534 2.48001 8.00004 2.48001C5.64671 2.48001 3.45337 3.86668 1.92671 6.26668C1.32671 7.20668 1.32671 8.78668 1.92671 9.72668C3.45337 12.1267 5.64671 13.5133 8.00004 13.5133Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    },
    {
      label: 'Edit',
      action: 'edit',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M7.33333 1.33334H6C2.66667 1.33334 1.33333 2.66668 1.33333 6.00001V10C1.33333 13.3333 2.66667 14.6667 6 14.6667H10C13.3333 14.6667 14.6667 13.3333 14.6667 10V8.66668"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.6933 2.01333L5.44 7.26666C5.24 7.46666 5.04 7.86 5 8.14666L4.71333 10.1533C4.60667 10.8733 5.12667 11.3867 5.84667 11.2867L7.85333 11C8.13333 10.96 8.52667 10.76 8.73333 10.56L13.9867 5.30666C14.8933 4.39999 15.32 3.34666 13.9867 2.01333C12.6533 0.679997 11.6 1.10666 10.6933 2.01333Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    },
    {
      label: 'Delete',
      action: 'delete',
      danger: true,
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M2 4H3.33333H14"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.6667 4V13.3333C12.6667 13.6869 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.6869 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.6869 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.66667 7.33333V11.3333"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.33333 7.33333V11.3333"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    }
  ];

  const columns = [
    {
      header: 'Role',
      accessor: 'name',
      headerClassName: 'text-sm font-medium text-[#222f2a] leading-[1.6] normal-case',
      className: 'text-base text-[#73827c]',
      render: (value) => <span className="text-base text-[#73827c]">{value}</span>
    },
    {
      header: 'Detail',
      accessor: 'description',
      headerClassName: 'text-sm font-medium text-[#222f2a] leading-[1.6] normal-case',
      className: 'text-base text-[#73827c]',
      render: (value) => (
        <span className="text-base text-[#73827c] truncate block max-w-[420px]">{value}</span>
      )
    },
    {
      header: 'Staff',
      accessor: 'staffCount',
      headerClassName: 'text-sm font-medium text-[#222f2a] leading-[1.6] normal-case text-right',
      align: 'right',
      render: (value) => <span className="text-base text-[#73827c]">{value.toString().padStart(2, '0')}</span>
    },
    {
      header: 'Date Created',
      accessor: 'dateCreated',
      headerClassName: 'text-sm font-medium text-[#222f2a] leading-[1.6] normal-case',
      render: (value) => <span className="text-base text-[#73827c]">{value}</span>
    },
    {
      header: 'Last Action',
      accessor: 'lastAction',
      headerClassName: 'text-sm font-medium text-[#222f2a] leading-[1.6] normal-case',
      render: (value) => <span className="text-base text-[#73827c]">{value}</span>
    },
    {
      header: 'Last Action by',
      accessor: 'lastActionBy',
      headerClassName: 'text-sm font-medium text-[#222f2a] leading-[1.6] normal-case',
      render: (value) => (
        <div className="flex items-center gap-2 min-w-[140px]">
          {value?.avatar ? (
            <img src={value.avatar} alt={value.name} className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-600">
              {(value?.name || 'U')[0].toUpperCase()}
            </div>
          )}
          <span className="text-base text-[#73827c] truncate">{value?.name || 'System'}</span>
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-4 w-full max-w-[1600px]">
      <div className="flex items-center justify-between">
        <h1 className="text-[32px] font-semibold text-[#212529] leading-[1.4] tracking-[-0.64px]">
          Roles & Permissions
        </h1>
        <button
          onClick={handleCreateRole}
          className="bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white px-6 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Create Role
        </button>
      </div>

      <DataTable
        columns={columns}
        data={displayRoles}
        actionMenuItems={actionMenuItems}
        onRowAction={handleRowAction}
        renderCell={(accessor, row) => {
          const col = columns.find(c => c.accessor === accessor);
          if (col && col.render) return col.render(row[accessor], row);
          return row[accessor];
        }}
        rowsPerPage={displayRoles.length || 10}
        showPagination={false}
        containerClassName="bg-white rounded-[12px] border border-[#f2f2f2]"
        tableWrapperClassName="overflow-x-auto"
        headerRowClassName="bg-[#fafafa]"
      />

      {/* Create/Edit Role Modal */}
      {isCreateModalOpen && (
        <CreateRoleModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingRole(null);
          }}
          onSave={handleSaveRole}
          editingRole={editingRole}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setRoleToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Role"
        message="Are you sure you want to delete this role? This action cannot be undone and will affect all users assigned to this role."
        itemName={roleToDelete?.name}
      />
    </div>
  );
};

// Create/Edit Role Modal Component
const CreateRoleModal = ({ isOpen, onClose, onSave, editingRole }) => {
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');

  React.useEffect(() => {
    if (editingRole) {
      setRoleName(editingRole.name || '');
      setRoleDescription(editingRole.description || '');
    } else {
      setRoleName('');
      setRoleDescription('');
    }
  }, [editingRole]);

  const handleSave = () => {
    if (!roleName.trim()) {
      alert('Please enter a role name');
      return;
    }
    onSave({
      name: roleName,
      description: roleDescription
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingRole ? 'Edit Role' : 'Create New Role'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="e.g., Admin, Reviewer, Approver"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={roleDescription}
              onChange={(e) => setRoleDescription(e.target.value)}
              placeholder="Describe what this role can do..."
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#40c191] to-[#5ed58d] rounded-lg hover:opacity-90 transition-opacity"
          >
            {editingRole ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;
